import React from 'react'
import {useCallback, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {DataObjectId, Hub} from 'yougile-model'
import {MovementClient} from '@client/movement-client'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {IconButton} from '@components/common/base/buttons'
import {Menu, MenuSimpleItemData} from '@components/common/base/menu'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {getTaskBoard} from '@extensions/task-data-helper'
import {moveTemplateApply} from './move-template-apply'

const TwIconButton = tw(IconButton)<{
  $isTaskColored?: boolean
  $isCompleted?: boolean
  $isOpen?: boolean
}>`
  -m-4
  
  ${p => {
    if (p.$isTaskColored) {
      return `!text-icon-primary ${
        p.$isCompleted
          ? 'opacity-[.38] hover:opacity-[.64] active:opacity-[.64]'
          : 'opacity-[.64] hover:opacity-100 active:opacity-100'
      }`
    }

    return `hover:!text-action-hover active:!text-action-pressed ${
      p.$isCompleted ? '!text-placeholder-new' : ''
    }`
  }}
`

export const MoveTemplatesMenu = ({
  taskId,
  completed,
  isTaskColored
}: {
  taskId: DataObjectId
  completed?: boolean
  isTaskColored?: boolean
}) => {
  const ref = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const openMenu = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsOpen(true)
    },
    [setIsOpen]
  )
  const closeMenu = useCallback(() => setIsOpen(false), [setIsOpen])

  const box = Sync.getCurrentSandbox()
  const task = box.get(taskId) as Hub
  const board = getTaskBoard(task)

  if (!task || !board) {
    return null
  }

  const list = MovementClient.listTemplatesInColumnEnabled(
    board.id,
    task.location
  )

  if (!MovementClient.hasFeature() || !list.length) {
    return null
  }

  if (SharedBoardsClient.isGuest()) {
    return null
  }

  return (
    <>
      <TwIconButton
        ref={ref}
        icon="IconArrowForwardUp"
        size="small"
        wrapperSize="big"
        color={'secondary'}
        $isTaskColored={isTaskColored}
        $isCompleted={completed}
        $isOpen={isOpen}
        forcedState={isOpen ? 'active' : undefined}
        onClick={openMenu}
      />

      {isOpen && (
        <MovementDropdown
          isOpen={isOpen}
          close={closeMenu}
          list={list}
          taskId={task.id}
          reference={ref}
        />
      )}
    </>
  )
}

const MovementDropdown = ({
  close,
  list,
  taskId,
  reference
}: {
  isOpen: boolean
  close: () => void
  list: Hub[]
  taskId: DataObjectId
  reference: React.RefObject<Element>
}) => {
  const menuItems: MenuSimpleItemData[] = list.map(x => ({
    id: `move-task-card-${x.id}`,
    icon: 'IconCornerUpRight',
    title: x?.data?.movementTemplate?.title || 'not-found',
    onClick: () => moveTemplateApply(taskId, x)
  }))

  return (
    <Dropdown
      reference={reference}
      hideArrow
      content={
        <Menu items={menuItems} onItemClicked={close} className="min-w-200" />
      }
      visible
      theme="new-menu"
      onVisibleChange={close}
    />
  )
}
